import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function Overview() {
  const [stats, setStats] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('token');
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : { role: { role_type: 'admin' } };
  const isAdmin = user.role?.role_type === 'admin' || user.role?.role_type === 'founder' || user.role?.role_type === 'deputy_admin' || user.role?.role_type === 'account_manager';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const [statsRes, accountsRes, transactionsRes] = await Promise.all([
        axios.get(`${API_URL}/stats`, config),
        axios.get(`${API_URL}/accounts`, config),
        axios.get(`${API_URL}/transactions`, config),
      ]);

      setStats(statsRes.data);
      setAccounts(accountsRes.data);
      setTransactions(transactionsRes.data.slice(0, 5));
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-20">Chargement...</div>;
  }

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-gray-900">Vue d'ensemble</h1>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Solde Total</h3>
            <span className="text-2xl">💰</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">
            {stats?.total_balance?.toLocaleString('fr-FR', { minimumFractionDigits: 2 }) || '0,00'} €
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Investissements</h3>
            <span className="text-2xl">📈</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">
            {stats?.total_investments?.toLocaleString('fr-FR', { minimumFractionDigits: 2 }) || '0,00'} €
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Comptes</h3>
            <span className="text-2xl">🏦</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">
            {stats?.accounts_count || 0}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Assurances</h3>
            <span className="text-2xl">🛡️</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">
            {stats?.investments_count || 0}
          </p>
        </div>
      </div>

      {/* Bank Info */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Informations Bancaires</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-gray-600">Nom de la banque</p>
            <p className="font-semibold">Banque Kreiz Breizh</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">BIC/SWIFT</p>
            <p className="font-semibold font-mono">BKBRBZP2001</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Code Banque</p>
            <p className="font-semibold font-mono">20041</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Code Pays IBAN</p>
            <p className="font-semibold">BZ (Bretagne)</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Siège</p>
            <p className="font-semibold">Rennes, Breizh</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Zone SEPA</p>
            <p className="font-semibold">Virements SEPA compatibles</p>
          </div>
        </div>
      </div>

      {/* Livre Bretonne */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl shadow-sm p-6 text-white">
        <h2 className="text-xl font-bold mb-2">Livret Breton £B</h2>
        <p className="text-blue-100 mb-4">Transition monétaire dans 3 ans</p>
        <p className="mb-4">
          La Bretagne quittera l'UE et adoptera la Livre Bretonne (£B) comme monnaie officielle.
        </p>
        <div className="bg-white/20 rounded-lg p-4 backdrop-blur-sm">
          <p className="font-semibold mb-2">Taux de change fixe</p>
          <p className="text-2xl font-bold">1 £B = 1,25 €</p>
          <p className="text-sm text-blue-100 mt-2">Comptes IBAN disponibles en £B</p>
        </div>
      </div>

      {/* Admin Stats */}
      {isAdmin && stats?.admin_stats && (
        <div className="bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl shadow-sm p-6 text-white">
          <h2 className="text-xl font-bold mb-4">👑 Statistiques Administrateur</h2>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white/20 rounded-lg p-4 backdrop-blur-sm">
              <p className="text-3xl font-bold">{stats.admin_stats.total_users}</p>
              <p className="text-sm">Utilisateurs totaux</p>
            </div>
            <div className="bg-white/20 rounded-lg p-4 backdrop-blur-sm">
              <p className="text-3xl font-bold">{stats.admin_stats.total_accounts}</p>
              <p className="text-sm">Comptes totaux</p>
            </div>
            <div className="bg-white/20 rounded-lg p-4 backdrop-blur-sm">
              <p className="text-3xl font-bold">{stats.admin_stats.total_transactions}</p>
              <p className="text-sm">Total des transactions</p>
            </div>
          </div>
        </div>
      )}

      {/* Bank Solidity - Admin Only */}
      {isAdmin && (
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl shadow-sm p-6 text-white">
          <h2 className="text-xl font-bold mb-2">Solidité de la Banque</h2>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <p className="text-sm text-green-100 mb-1">Fonds de réserve garanti</p>
              <p className="text-3xl font-bold">500 millions €</p>
            </div>
            <div className="text-6xl">🏛️</div>
          </div>
          <p className="text-sm text-green-100 mt-4">Banque Centrale Souveraine</p>
        </div>
      )}

      {/* Recent Transactions */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Transactions récentes</h2>
        <div className="space-y-3">
          {transactions.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Aucune transaction</p>
          ) : (
            transactions.map((tx) => (
              <div
                key={tx.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{tx.description}</p>
                  <p className="text-sm text-gray-500">
                    {new Date(tx.created_at).toLocaleDateString('fr-FR', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit'
                    })}
                  </p>
                </div>
                <div className="text-right">
                  <p className={`text-lg font-bold ${
                    tx.transaction_type === 'deposit' ? 'text-green-600' : 'text-gray-900'
                  }`}>
                    {tx.transaction_type === 'deposit' ? '+' : '-'}
                    {tx.amount?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} {tx.currency}
                  </p>
                  <p className={`text-xs px-2 py-1 rounded-full inline-block ${
                    tx.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {tx.status}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default Overview;
